import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { useBillingPortal } from "hooks/useBillingPortal"
import { useAlert } from "providers/AlertProvider"
import { adminApi, type HomeCategoryKey, type IntegrationQrCodeResponse } from "services/adminApi"
import { type Product } from "types"
import { capturePlatformEvent } from "services/platformAnalytics"
import { getGenericUserErrorMessage } from "utils/userFacingErrors"
import { useHomeQuery, useUpdateOnboardingMutation } from "@/features/home/api/useHome"
import { deriveHomeState, derivePublishTarget } from "@/features/home/homeState"
import { CATEGORY_ROUTES } from "@/features/home/categoryLinks"
import { useEnsureViewer3DDemoKeyMutation } from "@/features/viewer-3d/api/useViewer3D"
import { buildViewer3DDemoUrl } from "@/features/viewer-3d/demoUrl"
import {
  isTrustedProductDemoUrl,
  resolveProductDemoVariantId,
} from "@/pages/Products/ProductsPage/glassesDemoUrl"
import { ProductQrCodeDialog } from "@/pages/Products/Glasses/ProductQrCodeDialog"
import { OnboardingCard } from "./OnboardingCard"
import { LiveOverview } from "./LiveOverview"
import { ExploreSection } from "./ExploreSection"

const LIST_CATEGORY_PARAM: Record<Exclude<HomeCategoryKey, "viewer3d">, string> = {
  glasses: "GLASSES",
  makeup: "MAKEUP",
}

export const HomePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector(selectUserInfo)
  const billingPortal = useBillingPortal()
  const homeQuery = useHomeQuery()
  const updateOnboarding = useUpdateOnboardingMutation()
  const ensureViewer3DDemoKey = useEnsureViewer3DDemoKeyMutation()

  const demoRequestInFlightRef = useRef(false)
  const viewedEventSentRef = useRef(false)

  const [qrDialogOpen, setQrDialogOpen] = useState(false)
  const [qrGenerating, setQrGenerating] = useState(false)
  const [qrError, setQrError] = useState("")
  const [qrResult, setQrResult] = useState<IntegrationQrCodeResponse | null>(null)

  const state = homeQuery.data ? deriveHomeState(homeQuery.data) : null

  useEffect(() => {
    if (!state || viewedEventSentRef.current) return
    viewedEventSentRef.current = true
    capturePlatformEvent("home_viewed", { mode: state.mode, step: state.currentStep })
  }, [state])

  const handleSelectCategory = (category: HomeCategoryKey) => {
    capturePlatformEvent("home_category_selected", { category })
    updateOnboarding.mutate({ selectCategory: category })
  }

  const handleAddProduct = (category: HomeCategoryKey) => {
    navigate(CATEGORY_ROUTES[category].add)
  }

  const markDemoViewed = (category: HomeCategoryKey) => {
    capturePlatformEvent("home_demo_viewed", { category })
    updateOnboarding.mutate({ demoViewed: true })
  }

  const handleViewDemo = async () => {
    if (!state || demoRequestInFlightRef.current) return
    const category = state.selectedCategories.find(
      (key) => (homeQuery.data?.categories.find((item) => item.key === key)?.productsTotal ?? 0) > 0
    )
    if (!category) {
      showAlert({ error: true, text: t("home.onboarding.demoNoProducts") })
      return
    }

    // Pop-up safe: open the tab synchronously, then swap in the resolved URL
    // (the async work would otherwise trip the browser's popup blocker).
    const previewWindow = window.open("about:blank", "_blank")
    if (!previewWindow) {
      showAlert({ error: true, text: t("home.onboarding.demoError") })
      return
    }
    previewWindow.opener = null
    demoRequestInFlightRef.current = true

    try {
      if (category === "viewer3d") {
        const { token } = await ensureViewer3DDemoKey.mutateAsync()
        if (!previewWindow.closed) previewWindow.location.replace(buildViewer3DDemoUrl(token, null))
      } else {
        const merchantId = userInfo.merchantId?.trim()
        if (!merchantId) throw new Error("No merchant scope")
        const { items } = await adminApi.listProducts({
          category: LIST_CATEGORY_PARAM[category],
          limit: 1,
        })
        const product = items[0] as Product | undefined
        if (!product) throw new Error("No products to demo")
        const variantId = resolveProductDemoVariantId(product)
        const { previewUrl } = await adminApi.createTryOnPreviewLink({
          merchantId,
          productId: product.id,
          ...(variantId ? { variantId } : {}),
        })
        if (!isTrustedProductDemoUrl(previewUrl)) throw new Error("Untrusted preview URL")
        if (!previewWindow.closed) previewWindow.location.replace(previewUrl)
      }

      markDemoViewed(category)
    } catch {
      if (!previewWindow.closed) previewWindow.close()
      showAlert({ error: true, text: t("home.onboarding.demoError") })
    } finally {
      demoRequestInFlightRef.current = false
    }
  }

  const handlePublish = async () => {
    // A 3D-only catalog has nothing on the try-on QR landing — its store
    // integration is the embed snippet, so the step opens the 3D Integration
    // page instead of the QR modal (Tanya, Homepage ticket, 2026-08-05).
    if (homeQuery.data && derivePublishTarget(homeQuery.data) === "viewer3d-integration") {
      capturePlatformEvent("home_publish_confirmed", { target: "viewer3d-integration" })
      updateOnboarding.mutate({ publishConfirmed: true })
      navigate("/3d-viewer/integration")
      return
    }

    const merchantId = userInfo.merchantId?.trim()
    if (!merchantId) {
      showAlert({ error: true, text: getGenericUserErrorMessage(t) })
      return
    }

    setQrDialogOpen(true)
    setQrGenerating(true)
    setQrError("")
    setQrResult(null)

    try {
      setQrResult(await adminApi.generateIntegrationQrCode({ merchantId }))
      // The merchant has the link/QR in front of them — that is the design's
      // definition of "published to store" for onboarding purposes.
      capturePlatformEvent("home_publish_confirmed", { target: "qr" })
      updateOnboarding.mutate({ publishConfirmed: true })
    } catch {
      setQrError(getGenericUserErrorMessage(t))
    } finally {
      setQrGenerating(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1120px] min-w-0 p-4 md:p-6" data-testid="home-page">
      <div className="mb-6 flex items-center justify-between border-b pb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("home.title")}</h1>
        {billingPortal.canManageBilling && (
          <Button
            variant="outline"
            onClick={() => void billingPortal.openBillingPortal()}
            disabled={billingPortal.isPortalLoading}
            data-testid="home-view-plans"
          >
            {t("home.viewPlans")}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-8">
        {homeQuery.isPending ? (
          <div className="flex flex-col gap-8" data-testid="home-loading">
            <Skeleton width="w-full" height="h-[420px]" />
            <Skeleton width="w-full" height="h-[108px]" />
          </div>
        ) : homeQuery.isError || !state || !homeQuery.data ? (
          <div
            className="flex min-h-[40vh] flex-col items-center justify-center gap-4"
            data-testid="home-error"
          >
            <p className="text-sm text-muted-foreground">{t("home.loadError")}</p>
            <Button variant="outline" onClick={() => void homeQuery.refetch()}>
              {t("home.retry")}
            </Button>
          </div>
        ) : state.mode === "onboarding" ? (
          <OnboardingCard
            state={state}
            onAddProduct={handleAddProduct}
            onPublish={() => void handlePublish()}
            onSelectCategory={handleSelectCategory}
            onViewDemo={() => void handleViewDemo()}
          />
        ) : (
          <LiveOverview
            data={homeQuery.data}
            state={state}
            onAddCategory={handleSelectCategory}
            onAddProduct={handleAddProduct}
          />
        )}

        <ExploreSection />
      </div>

      <ProductQrCodeDialog
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        isGenerating={qrGenerating}
        error={qrError}
        result={qrResult}
      />
    </div>
  )
}
